import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Place as PlaceIcon,
  Assessment as AssessmentIcon,
  RateReview as RateReviewIcon,
  Article as ArticleIcon,
  AccountCircle as AccountIcon,
} from '@mui/icons-material';
import FeedLayout from '../../components/feed/FeedLayout';
import { getFeedSummary } from '../../services/feedApi';

const moduleCards = [
  {
    title: 'Overview',
    path: '/feed/overview',
    icon: <DashboardIcon sx={{ fontSize: 32 }} />,
  },
  {
    title: 'Regions',
    path: '/feed/regions',
    icon: <PlaceIcon sx={{ fontSize: 32 }} />,
  },
  {
    title: 'Reports',
    path: '/feed/reports',
    icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
  },
  {
    title: 'Posting',
    path: '/feed/posting',
    icon: <ArticleIcon sx={{ fontSize: 32 }} />,
  },
  {
    title: 'Review Management',
    path: '/feed/reviews',
    icon: <RateReviewIcon sx={{ fontSize: 32 }} />,
  },
  {
    title: 'Account',
    path: '/feed/account',
    icon: <AccountIcon sx={{ fontSize: 32 }} />,
  },
];

const FeedHome = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const data = await getFeedSummary();
        setSummary(data);
      } catch (err) {
        console.error('Error fetching feed summary:', err);
        setError('Unable to load summary. The API may not be available yet.');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  return (
    <FeedLayout>
      <Box sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Welcome to The Trusted Feed
          </Typography>

          {/* Logo / Brand */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Avatar
              sx={{
                bgcolor: '#3b5998',
                width: 80,
                height: 80,
                fontSize: '2rem',
                fontWeight: 'bold',
              }}
            >
              TF
            </Avatar>
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a2b4a',
              mb: 1,
            }}
          >
            The Trusted Feed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your multi-location GBP tool.
          </Typography>
        </Box>

        {/* Summary Stats */}
        {error && (
          <Alert severity="info" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Grid container spacing={3} sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : summary ? (() => {
          const regionCount = summary.regionCount ?? Object.keys(summary.regions || {}).length;
          const totalLocations = summary.totalLocations ?? 0;
          const avgLocationsPerRegion = regionCount > 0 ? Math.round(totalLocations / regionCount) : 0;

          return (
          <Grid container spacing={3} sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 2,
                  bgcolor: 'rgba(59, 89, 152, 0.05)',
                  border: '1px solid rgba(59, 89, 152, 0.1)',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a' }}>
                  {totalLocations}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Locations
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 2,
                  bgcolor: 'rgba(59, 89, 152, 0.05)',
                  border: '1px solid rgba(59, 89, 152, 0.1)',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a' }}>
                  {regionCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Regions
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 2,
                  bgcolor: 'rgba(59, 89, 152, 0.05)',
                  border: '1px solid rgba(59, 89, 152, 0.1)',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a' }}>
                  {avgLocationsPerRegion}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Locations Per Region
                </Typography>
              </Card>
            </Grid>
          </Grid>
          );
        })() : null}

        {/* Module Cards */}
        <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 900, mx: 'auto' }}>
          {moduleCards.map((card) => (
            <Grid item xs={6} sm={4} md={4} key={card.title}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#3b5998',
                    boxShadow: '0 4px 12px rgba(59, 89, 152, 0.15)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <CardActionArea
                  onClick={() => navigate(card.path)}
                  sx={{ height: '100%', p: 3, textAlign: 'center' }}
                >
                  <CardContent>
                    <Box sx={{ color: '#3b5998', mb: 2 }}>{card.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a2b4a' }}>
                      {card.title}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </FeedLayout>
  );
};

export default FeedHome;
